import React, {useEffect, useMemo, useState} from "react";
import {Button} from "react-bootstrap";
import {useLocation, useNavigate} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import useCrud from "../../../../hooks/useCrud";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import "../../../../css/CompanyForms.css";

const Company = () => {
  const dataUrl = "/company";
  const legalEntitiesUrl = "/legal-entities";
  const taxOfficesUrl = "/tax-offices";
  const countriesUrl = "/countries";

  const navigate = useNavigate();
  const location = useLocation();
  const {updateItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({});
  const [backupItem, setBackupItem] = useState({});
  const [legalEntitiesList, setLegalEntitiesList] = useState([]);
  const [taxOfficesList, setTaxOfficesList] = useState([]);
  const [countriesList, setCountriesList] = useState([]);
  const [invalidFields, setInvalidFields] = useState(new Set());

  const state = {itemsList, setItems};

  const sections = [
    {
      title: "Nazwa firmy",
      fields: ["fullName", "shortName"]
    },
    {
      title: "Dane rejestracyjne",
      fields: ["legalEntity", "taxOffice", "regon", "nip", "krs", "insuranceNumber"]
    },
    {
      title: "Adres siedziby",
      fields: ["primaryStreet", "primaryPostalCode", "primaryCity", "primaryCountry"]
    },
    {
      title: "Adres korespondencyjny",
      fields: ["secondaryStreet", "secondaryPostalCode", "secondaryCity", "secondaryCountry"]
    },
    {
      title: "Dane kontaktowe",
      fields: ["email", "website", "primaryPhone", "secondaryPhone"]
    },
    {
      title: "Rozliczenia",
      fields: ["bankAccount"]
    }
  ];

  const labels = {
    fullName: "Pełna nazwa",
    shortName: "Skrócona nazwa",
    legalEntity: "Forma prawna",
    taxOffice: "Urząd skarbowy",
    regon: "REGON",
    nip: "NIP",
    krs: "KRS",
    insuranceNumber: "Numer ubezpieczenia",
    primaryStreet: "Ulica",
    primaryPostalCode: "Kod pocztowy",
    primaryCity: "Miasto",
    primaryCountry: "Państwo",
    secondaryStreet: "Ulica",
    secondaryPostalCode: "Kod pocztowy",
    secondaryCity: "Miasto",
    secondaryCountry: "Państwo",
    email: "Adres e-mail",
    website: "Strona WWW",
    primaryPhone: "Telefon główny",
    secondaryPhone: "Telefon dodatkowy",
    bankAccount: "Konto bankowe"
  };

  const requiredFields = new Set([
    "fullName",
    "shortName",
    "regon",
    "nip",
    "krs",
    "insuranceNumber",
    "primaryStreet",
    "primaryPostalCode",
    "primaryCity",
    "secondaryStreet",
    "secondaryPostalCode",
    "secondaryCity",
    "bankAccount"
  ]);

  const relationFields = {
    legalEntity: {items: legalEntitiesList, placeholder: "Wybierz formę prawną"},
    taxOffice: {items: taxOfficesList, placeholder: "Wybierz urząd skarbowy"},
    primaryCountry: {items: countriesList, placeholder: "Wybierz państwo"},
    secondaryCountry: {items: countriesList, placeholder: "Wybierz państwo"}
  };

  const normalize = item => ({
    id: item?.id ?? 1,
    fullName: item?.fullName ?? "",
    shortName: item?.shortName ?? "",
    regon: item?.regon ?? "",
    nip: item?.nip ?? "",
    krs: item?.krs ?? "",
    insuranceNumber: item?.insuranceNumber ?? "",
    legalEntityId: item?.legalEntity?.id ?? null,
    taxOfficeId: item?.taxOffice?.id ?? null,
    current: item?.current ?? null,
    notes: item?.notes ?? "",
    representativeId: item?.representative?.id ?? null,
    primaryStreet: item?.primaryStreet ?? "",
    primaryPostalCode: item?.primaryPostalCode ?? "",
    primaryCity: item?.primaryCity ?? "",
    primaryCountryId: item?.primaryCountry?.id ?? null,
    secondaryStreet: item?.secondaryStreet ?? "",
    secondaryPostalCode: item?.secondaryPostalCode ?? "",
    secondaryCity: item?.secondaryCity ?? "",
    secondaryCountryId: item?.secondaryCountry?.id ?? null,
    email: item?.email ?? "",
    website: item?.website ?? "",
    primaryPhone: item?.primaryPhone ?? "",
    secondaryPhone: item?.secondaryPhone ?? "",
    bankAccount: item?.bankAccount ?? ""
  });

  const isDirty = useMemo(
    () => JSON.stringify(normalize(currentItem)) !== JSON.stringify(normalize(backupItem)),
    [currentItem, backupItem]
  );

  useEffect(() => {
    const load = async () => {
      const [companyResponse, legalEntitiesResponse, taxOfficesResponse, countriesResponse] = await Promise.all([
        getItems(dataUrl),
        getItems(legalEntitiesUrl),
        getItems(taxOfficesUrl),
        getItems(countriesUrl)
      ]);

      if ([companyResponse, legalEntitiesResponse, taxOfficesResponse, countriesResponse].some(response => response.status === 401 || response.status === 403)) {
        navigate("/login", {state: {from: location}, replace: true});
        return;
      }

      const success = companyResponse.status === 200 && legalEntitiesResponse.status === 200 && taxOfficesResponse.status === 200 && countriesResponse.status === 200;
      if (!success) {
        toastError("Nie udało się pobrać danych firmy");
        setLoading(false);
        return;
      }

      const company = companyResponse.data || {};
      setItems(company?.id ? [company] : []);
      setCurrentItem(company);
      setBackupItem(company);
      setLegalEntitiesList(legalEntitiesResponse.data || []);
      setTaxOfficesList(taxOfficesResponse.data || []);
      setCountriesList(countriesResponse.data || []);
      setLoading(false);
    };

    load();
  }, []);

  const clearInvalid = field => {
    if (!invalidFields.has(field)) return;
    setInvalidFields(previous => {
      const next = new Set(previous);
      next.delete(field);
      return next;
    });
  };

  const setField = (field, value) => {
    clearInvalid(field);
    setCurrentItem(previous => ({...previous, [field]: value}));
  };

  const setRelationField = (field, id) => {
    const config = relationFields[field];
    const numericId = id ? Number(id) : null;
    const selected = numericId ? config.items.find(item => Number(item.id) === numericId) || {id: numericId} : null;
    setField(field, selected);
  };

  const restore = () => {
    setCurrentItem(backupItem);
    setInvalidFields(new Set());
  };

  const validate = () => {
    const invalid = new Set();
    requiredFields.forEach(field => {
      const value = currentItem?.[field];
      if (value === null || value === undefined || String(value).trim() === "") invalid.add(field);
    });
    setInvalidFields(invalid);
    if (invalid.size > 0) toastError("Uzupełnij wymagane dane firmy");
    return invalid.size === 0;
  };

  const save = async () => {
    if (!isDirty || saving || !validate()) return;

    setSaving(true);
    const payload = {
      id: currentItem.id || 1,
      fullName: currentItem.fullName,
      shortName: currentItem.shortName,
      regon: currentItem.regon,
      nip: currentItem.nip,
      krs: currentItem.krs,
      insuranceNumber: currentItem.insuranceNumber,
      legalEntity: currentItem.legalEntity?.id ? {id: currentItem.legalEntity.id} : null,
      taxOffice: currentItem.taxOffice?.id ? {id: currentItem.taxOffice.id} : null,
      current: currentItem.current,
      notes: currentItem.notes,
      representative: currentItem.representative?.id ? {id: currentItem.representative.id} : null,
      primaryStreet: currentItem.primaryStreet,
      primaryPostalCode: currentItem.primaryPostalCode,
      primaryCity: currentItem.primaryCity,
      primaryCountry: currentItem.primaryCountry?.id ? {id: currentItem.primaryCountry.id} : null,
      secondaryStreet: currentItem.secondaryStreet,
      secondaryPostalCode: currentItem.secondaryPostalCode,
      secondaryCity: currentItem.secondaryCity,
      secondaryCountry: currentItem.secondaryCountry?.id ? {id: currentItem.secondaryCountry.id} : null,
      email: currentItem.email,
      website: currentItem.website,
      primaryPhone: currentItem.primaryPhone,
      secondaryPhone: currentItem.secondaryPhone,
      bankAccount: currentItem.bankAccount
    };

    const response = await updateItem(`${dataUrl}/1`, payload, state);
    setSaving(false);

    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
      return;
    }

    if (response.status < 200 || response.status >= 300) {
      toastError(typeof response.data === "string" ? response.data : response.data?.message || "Nie udało się zapisać danych firmy");
      return;
    }

    const saved = response.data || payload;
    setCurrentItem(saved);
    setBackupItem(saved);
    setItems([saved]);
    setInvalidFields(new Set());
    toastSuccess("Dane firmy zostały zapisane");
  };

  const renderControl = field => {
    const relation = relationFields[field];
    const invalid = invalidFields.has(field);

    if (relation) {
      return (
        <select
          id={field}
          className={`company-inline-input ${invalid ? "is-invalid" : ""}`}
          value={currentItem?.[field]?.id || ""}
          onChange={event => setRelationField(field, event.target.value)}
        >
          <option value="">{relation.placeholder}</option>
          {relation.items.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      );
    }

    return (
      <input
        id={field}
        className={`company-inline-input ${invalid ? "is-invalid" : ""}`}
        type={field === "email" ? "email" : field === "website" ? "url" : "text"}
        value={currentItem?.[field] ?? ""}
        onChange={event => setField(field, event.target.value)}
      />
    );
  };

  const renderSection = section => (
    <section key={section.title} className="company-inline-section">
      <div className="company-inline-section-title">{section.title}</div>
      <div className="company-inline-fields">
        {section.fields.map(field => (
          <div key={field} className={`company-inline-field ${field === "bankAccount" ? "is-wide" : ""}`}>
            <label className="company-inline-label" htmlFor={field}>
              {labels[field]}
              {requiredFields.has(field) && <span className="company-required"> *</span>}
            </label>
            <div className="company-inline-value">{renderControl(field)}</div>
          </div>
        ))}
      </div>
    </section>
  );

  if (loading) {
    return (
      <div id="layoutSidenav_content" className="company-inline-page">
        <AppComponentCardHeader title="Informacje o firmie"/>
        <div className="container-fluid px-4 py-3 company-inline-container"><LoadingDataDiv/></div>
      </div>
    );
  }

  return (
    <div id="layoutSidenav_content" className="company-inline-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Informacje o firmie"/>
      <div className="container-fluid px-4 py-3 company-inline-container">
        <div className="card shadow-sm bg-white rounded-2">
          <div className="company-inline-editor">
            <div className="company-inline-header">
              <div>
                <strong>Dane firmy</strong>
                <span>Dane można edytować bezpośrednio w tym widoku. Zapis jest aktywny dopiero po wprowadzeniu zmian.</span>
              </div>
              <div className="d-flex gap-2 company-inline-actions">
                <Button type="button" variant="outline-secondary" onClick={restore} disabled={!isDirty || saving}>Przywróć</Button>
                <Button type="button" onClick={save} disabled={!isDirty || saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button>
              </div>
            </div>

            <div className="company-inline-grid">
              {sections.map(renderSection)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Company;
